/* eslint-disable no-console */
import axios, {
  AxiosError,
  AxiosHeaderValue,
  AxiosInstance,
  AxiosRequestConfig,
  HeadersDefaults,
  InternalAxiosRequestConfig,
} from "axios";
import jwt_decode from "jwt-decode";

import LOCAL_STORAGE from "@/constants/local-storage";
import ROUTE_URLS from "@/constants/route-urls";
import { IDecodedToken } from "@/interfaces/auth.interfaces";
import { isTokenExpired } from "@/utils/token-utils";

export type TRequestData = Record<never, unknown>;

export type TRequestHeaders = HeadersDefaults & {
  [key: string]: AxiosHeaderValue;
};

// Flag para prevenir loops infinitos de refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (error?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const logout = () => {
  localStorage.removeItem(LOCAL_STORAGE.accessTokenKey);
  localStorage.removeItem(LOCAL_STORAGE.refreshTokenKey);
  localStorage.removeItem(LOCAL_STORAGE.expiredPassword);

  const pathname = window.location.pathname;
  const lang = (pathname.split("/").pop() || "PT-BR") as "PT-BR";
  const isLoginPage = pathname.includes("/login/");

  if (!isLoginPage) {
    window.location.href = ROUTE_URLS.login(lang);
  }
};

export class HttpClient {
  private axiosInstance: AxiosInstance;

  constructor(private baseUrl: string) {
    this.axiosInstance = axios.create({ baseURL: baseUrl });
    this.setupInterceptors();
  }

  private async refreshAccessToken(refreshToken: string): Promise<string | null> {
    try {
      const response = await axios.post(`${this.baseUrl}/v1/login/refreshtoken`, {
        refreshToken: refreshToken,
      });

      const { refreshToken: newRefreshToken, accessToken: newAccessToken } = response.data;

      if (!newRefreshToken || !newAccessToken) {
        return null;
      }

      localStorage.setItem(LOCAL_STORAGE.accessTokenKey, newAccessToken);
      localStorage.setItem(LOCAL_STORAGE.refreshTokenKey, newRefreshToken);

      return newAccessToken;
    } catch (error) {
      console.error("Error refreshing access token:", error);
      return null;
    }
  }

  private setupInterceptors() {
    // Interceptor de request - adiciona token nas requisições
    this.axiosInstance.interceptors.request.use(async (config) => {
      const pathname = window.location.pathname;

      if (pathname.includes("/login/")) return config;

      let accessToken = localStorage.getItem(LOCAL_STORAGE.accessTokenKey);
      const refreshToken = localStorage.getItem(LOCAL_STORAGE.refreshTokenKey);

      if (!accessToken) {
        return config;
      }

      try {
        let decodedToken: IDecodedToken = jwt_decode(accessToken);

        if (isTokenExpired(decodedToken) && refreshToken) {
          const newAccessToken = await this.refreshAccessToken(refreshToken);
          if (newAccessToken) {
            accessToken = newAccessToken;
            decodedToken = jwt_decode(newAccessToken);
          }
        }

        if (!isTokenExpired(decodedToken) && accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
      } catch (error) {
        console.error("Error decoding token:", error);
      }

      return config;
    });

    // Interceptor de response - trata erros 401 e faz refresh automático
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // Se não for erro 401 ou já tentou refresh, rejeita o erro
        if (!error.response || error.response.status !== 401 || originalRequest._retry) {
          return Promise.reject(error);
        }

        // Se a requisição é para refresh token, não tenta refresh novamente
        if (originalRequest.url?.includes("/refreshtoken")) {
          logout();
          return Promise.reject(error);
        }

        // Se já está fazendo refresh, adiciona à fila
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              return this.axiosInstance(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = localStorage.getItem(LOCAL_STORAGE.refreshTokenKey);

        if (!refreshToken) {
          processQueue(error, null);
          logout();
          return Promise.reject(error);
        }

        const newAccessToken = await this.refreshAccessToken(refreshToken);

        if (!newAccessToken) {
          processQueue(error, null);
          logout();
          return Promise.reject(error);
        }

        processQueue(null, newAccessToken);
        isRefreshing = false;

        // Atualiza o header e refaz a requisição original
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        return this.axiosInstance(originalRequest);
      }
    );
  }

  public buildHeaders(_accessToken?: string) {
    let accessToken: string | null | undefined = _accessToken;
    if (!_accessToken) {
      accessToken = localStorage ? localStorage.getItem(LOCAL_STORAGE.accessTokenKey) : "";
    }

    const headers = {
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    };

    return headers;
  }

  async request<T = never, R = TRequestData>(
    method: string,
    path: string,
    body?: R,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const headers = this.buildHeaders();

    const response = await this.axiosInstance.request<T>({
      method,
      url: path,
      data: body,
      headers,
      ...config,
    });

    return response.data;
  }

  async post<T = never>(
    path: string,
    body?: TRequestData,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const headers = this.buildHeaders();
    return this.request<T>("post", path, body, { headers, ...config });
  }

  async put<T = never>(path: string, body?: TRequestData, config?: AxiosRequestConfig): Promise<T> {
    const headers = this.buildHeaders();
    return this.request<T>("put", path, body, { headers, ...config });
  }

  async get<T = never>(path: string, config?: AxiosRequestConfig): Promise<T> {
    const headers = this.buildHeaders();
    return this.request<T>("get", path, undefined, { headers, ...config });
  }

  async delete<T = never>(path: string, config?: AxiosRequestConfig): Promise<T> {
    const headers = this.buildHeaders();
    return this.request<T>("delete", path, undefined, { headers, ...config });
  }
}

const apiBaseUrl = import.meta.env.VITE_API_URL || "";
const httpRequest = new HttpClient(apiBaseUrl);
export default httpRequest;

const apiAppBaseUrl = import.meta.env.VITE_APP_API_URL || "";
export const httpRequestApp = new HttpClient(apiAppBaseUrl);
